import axios from 'axios';

const TOKEN_KEY = 'token';
const USER_KEY = 'userId';
const REASON_KEY = 'signOutReason';
const RENEW_HEADER = 'X-Session-Renewed';

export const SESSION_EXPIRED =
  'Your session ended, so you were signed out. Please sign in again.';

function tokenExp(token) {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload)).exp || 0;
  } catch {
    return 0;
  }
}

function absorbRenewal(token) {
  if (!token) return;
  const exp = tokenExp(token);
  if (exp <= Date.now() / 1000) return;
  const current = getToken();
  if (current && tokenExp(current) >= exp) return;
  try { localStorage.setItem(TOKEN_KEY, token); } catch { /* private mode */ }
}

export function takeSignOutReason() {
  try {
    const reason = sessionStorage.getItem(REASON_KEY);
    if (reason) sessionStorage.removeItem(REASON_KEY);
    return reason;
  } catch {
    return null;
  }
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function isAuthenticated() {
  return !!getToken();
}

export function setSession({ token, userId }) {
  localStorage.setItem(TOKEN_KEY, token);
  if (userId != null) localStorage.setItem(USER_KEY, String(userId));
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

let redirecting = false;
function toSignIn(reason) {
  clearSession();
  if (reason) {
    try { sessionStorage.setItem(REASON_KEY, reason); } catch { /* private mode */ }
  }
  if (redirecting || window.location.pathname === '/') return;
  redirecting = true;
  window.location.replace('/');
}

const isApiPath = (url) => {
  try {
    // Relative URLs are same-origin by construction; absolute ones have to be
    // checked, or a third-party host would receive our session token.
    const u = new URL(url, window.location.origin);
    return u.origin === window.location.origin && u.pathname.startsWith('/api/');
  } catch {
    return false;
  }
};

export function installAuth() {
  axios.interceptors.request.use((config) => {
    const token = getToken();
    if (token && isApiPath(config.url || '')) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  axios.interceptors.response.use(
    (r) => {
      absorbRenewal(r.headers?.[RENEW_HEADER.toLowerCase()]);
      return r;
    },
    (error) => {
      if (error.response?.status === 401) toSignIn(SESSION_EXPIRED);
      return Promise.reject(error);
    }
  );

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url || '';
    const token = getToken();
    let opts = init;
    if (token && isApiPath(url)) {
      opts = { ...init, headers: { ...(init.headers || {}), Authorization: `Bearer ${token}` } };
    }
    const res = await nativeFetch(input, opts);
    if (isApiPath(url)) {
      absorbRenewal(res.headers.get(RENEW_HEADER));
      if (res.status === 401) toSignIn(SESSION_EXPIRED);
    }
    return res;
  };
}
